import React, { useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
  formatMedicalText,
  formatAmount,
  useDeviceType,
  DeviceTypes,
} from 'common';
import {
  Headings,
  Paragraph,
  Table,
  Accordion,
} from '@sentara-health/optimahealth_web';
import { ClaimResponse } from '@sentara-health/sentara-api-hooks-core';

interface ClaimTotals {
  totalCharges: string;
  totalNotCovered: string;
  totalCovered: string;
  totalDeductible: string;
  copayPlusCoinsurance: number;
  totalPaidByPlan: string;
  patientResponsibility: string;
  datePaymentSentToProvider: string;
}

interface SuperEOBBreakdownProps {
  claimData: ClaimResponse | null;
  totals: ClaimTotals | null;
  className?: string;
}

const extractAdjudicationValues = (adjudications: any[] = []) => {
  const result = {
    serviceType: '—',
    charged: 0,
    paidByPlan: 0,
    responsibility: 0,
  };

  for (const adj of adjudications) {
    const codingData = adj?.category?.coding;
    const coding = Array.isArray(codingData) ? codingData[0] : codingData;
    const code =
      typeof coding?.code === 'string' ? coding.code.toLowerCase() : '';
    const val = Number(adj?.amount?.value ?? 0);

    switch (code) {
      case 'itemservicetype':
        result.serviceType = coding?.display ?? '—';
        break;
      case 'charged':
        result.charged = val;
        break;
      case 'paidbyplan':
        result.paidByPlan = val;
        break;
      case 'patientresponsibility':
        result.responsibility = val;
        break;
    }
  }

  return result;
};

const SuperEOBBreakdown: React.FC<SuperEOBBreakdownProps> = ({
  claimData,
  totals,
  className = '',
}) => {
  const { t } = useTranslation();
  const deviceType = useDeviceType();
  const isMobile = deviceType === DeviceTypes.MOBILE;

  const serviceItems = useMemo(() => {
    const rawItems = claimData?.item ?? [];
    const items = Array.isArray(rawItems) ? rawItems : [rawItems];

    return items
      .filter(Boolean)
      .map((item) => extractAdjudicationValues(item.adjudication ?? []));
  }, [claimData]);

  const tableRows = useMemo(() => {
    const rows: any[] = [];

    serviceItems.forEach(
      ({ serviceType, charged, paidByPlan, responsibility }) => {
      rows.push([
        {
            text: (
              <th scope="row" className="ps-4">
                {formatMedicalText(serviceType)}
              </th>
            ),
        },
        {
            text: (
              <span className="d-block text-end m-0">
                ${formatAmount(charged)}
              </span>
            ),
        },
        {
            text: (
              <span className="d-block text-end m-0">
                ${formatAmount(paidByPlan)}
              </span>
            ),
        },
        {
            text: (
              <span className="d-block text-end m-0">
                ${formatAmount(responsibility)}
              </span>
            ),
        },
      ]);
      }
    );

    if (totals) {
      rows.push([
        {
          text: <th scope="row" className="ps-4 fw-bold m-0">{t("medicalClaims.claimTotal")}</th>
        },
        {
          text: (
            <span className="d-block text-end m-0">${totals.totalCharges}</span>
          ),
        },
        {
          text: (
            <span className="d-block text-end m-0">
              ${totals.totalPaidByPlan}
            </span>
          ),
        },
        {
          text: (
            <Paragraph
              text={`$${totals.patientResponsibility ?? '0'}`}
              variant="bold"
              className="d-block text-end m-0"
              textColor="secondary"
            />
          ),
        },
      ]);
    }

    return rows;
  }, [serviceItems, totals, t]);

const header = [
    {
      text: (
        <span className="d-block text-start m-0">
          {t('medicalClaims.serviceType')}
        </span>
      ),
    },
    {
      text: (
        <span className="d-block text-end m-0">
          {t('medicalClaims.amountCharged')}
        </span>
      ),
    },
    {
      text: (
        <span className="d-block text-end m-0">
          {t('medicalClaims.insurancePaid')}
        </span>
      ),
    },
    {
      text: (
        <span className="d-block text-end m-0">
          {t('medicalClaims.youMayOweText')}
        </span>
      ),
    },
];

  const accordionItems = serviceItems.map(
    ({ serviceType, charged, paidByPlan, responsibility }, index) => ({
      id: `svc-${index}`,

      title: (
        <div className="d-flex flex-row align-items-center w-100">
          <Paragraph
              className="text-nowrap mt-3"
            textColor="secondary"
              text={
                <Trans
                  i18nKey="medicalClaims.typeofServiceaccordionTitle"
                  values={{ serviceType: formatMedicalText(serviceType) }}
                  components={{ strong: <strong /> }}
                />
              }
            />
        </div>
      ),

      content: (
        <div className="p-3">
          <div className="d-flex justify-content-between mb-2">
            <Paragraph text={t("medicalClaims.amountCharged")} />
            <Paragraph
              className="text-nowrap"
              text={
                <Trans
                  i18nKey="medicalClaims.amountChargedAccordian"
                  values={{ amountCharged: formatAmount(charged) }}
                />
              }
            />
          </div>
          <div className="d-flex justify-content-between mb-2">
            <Paragraph text={t("medicalClaims.insurancePaid")} />
            <Paragraph
              className="text-nowrap"
              text={
                <Trans
                  i18nKey="medicalClaims.insurancePaidAccordian"
                  values={{ insurancePaid: formatAmount(paidByPlan) }}
                />
              }
            />
          </div>
          <div className="d-flex justify-content-between">

            <Paragraph text={t("medicalClaims.youMayOweText")} variant="bold" />

            <Paragraph
              textColor="secondary"
              level={3}
              variant="bold"
              text={
                <Trans
                  i18nKey="medicalClaims.youMayOweAccordian"
                  values={{ youMayOwe: formatAmount(responsibility) }}
                  components={{ strong: <strong /> }}
                />
              }
            />
          </div>
        </div>
      ),
    })
  );

  return (
    <div className={`mb-md-5 ${className}`} data-testid="SuperEOBBreakdown">
      <Headings
        level={3}
        text={t("medicalClaims.priceBreakdown")}
        variant="semiBold"
        className="mb-4"
      />

      {isMobile ? (     
          <Accordion
            id="claimsFilterAccordion"
            items={accordionItems}
            initialActiveIndex={0}
            isFiltered
          />
      ) : (
          <Table
            header={header}
            rows={tableRows}
            className="shadow-none border rounded-4"
            headerBgColor="secondary"
            headerTextColor="primary"
          />
      )}
    </div>
  );
};

export default SuperEOBBreakdown;
